
import VueI18n from "vue-i18n";
import { TypedVue } from "@/store/types";
import { Component, Inject, Prop } from "vue-property-decorator";
import { ProjectTheme, ProjectReportData, TeamEffectivenessV2ReportData, V2Project } from "@/store/admin/adminTypes";
import { buildLeaderSummaries, buildSubgroups, buildCompetencyStats, filterLeaders, LeaderSummary, Subgroup, CompetencyStat, ReportFilters } from "./shared";
import SummaryTab from "./SummaryTab.vue";
import TeamProfileTab from "./TeamProfileTab.vue";
import ByLeaderTab from "./ByLeaderTab.vue";
import NextStepsTab from "./NextStepsTab.vue";

@Component({
  components: { SummaryTab, TeamProfileTab, ByLeaderTab, NextStepsTab },
})
export default class TeamEffectivenessReportV2 extends TypedVue {
  // Shadows the global $i18n with the per-report instance provided by the view,
  // so $t() also resolves the merged project-report:team-effectiveness v2 literals.
  @Inject("$i18n") readonly $i18n!: VueI18n;

  @Prop({ required: true })
  readonly report!: ProjectReportData;

  @Prop({ required: true })
  readonly project!: V2Project;

  public reportData: TeamEffectivenessV2ReportData | null = null;
  public tabIndex = 0;
  public printing = false;
  public filters: ReportFilters = { activeLeaderIds: [], activeSubgroupIds: [], showSubgroups: false, hideNames: false };

  public tabs = [
    { key: "summary", label: "Tabs::Summary" },
    { key: "profile", label: "Tabs::TeamProfile" },
    { key: "leader", label: "Tabs::ByLeader" },
    { key: "next", label: "Tabs::NextSteps" },
  ];

  get projectTheme(): ProjectTheme {
    return (this.project?.theme as ProjectTheme) ?? ProjectTheme.DEFAULT_TA;
  }

  get leaders(): LeaderSummary[] {
    return this.reportData ? buildLeaderSummaries(this.reportData) : [];
  }

  get subgroups(): Subgroup[] {
    return this.reportData ? buildSubgroups(this.reportData, this.leaders) : [];
  }

  get visibleLeaders(): LeaderSummary[] {
    return filterLeaders(this.leaders, this.subgroups, this.filters);
  }

  // Whole-team stats (Summary never filters).
  get allStats(): CompetencyStat[] {
    return buildCompetencyStats(this.leaders, this.reportData?.traitPriorities ?? {}, this.traitName);
  }

  // Stats for the currently filtered leaders (Team Profile + By Leader).
  get visibleStats(): CompetencyStat[] {
    return buildCompetencyStats(this.visibleLeaders, this.reportData?.traitPriorities ?? {}, this.traitName);
  }

  get generatedDate(): string {
    const raw = (this.report as unknown as { dateCreated?: string; date_created?: string })?.dateCreated ?? (this.report as unknown as { date_created?: string })?.date_created;
    const date = raw ? new Date(raw) : new Date();
    return date.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" });
  }

  traitName(trait: string): string {
    return this.$t(`Comparison::TraitChart::XAxisLabel::${trait}`).toString();
  }

  updateFilters(patch: Partial<ReportFilters>): void {
    this.filters = { ...this.filters, ...patch };
  }

  resetFilters(): void {
    this.filters = {
      activeLeaderIds: this.leaders.map((l) => l.id),
      activeSubgroupIds: this.subgroups.map((s) => s.id),
      showSubgroups: false,
      hideNames: this.filters.hideNames,
    };
  }

  viewGuide(): void {
    window.open("https://assets.system-3.com/web-assets/report/team-effectiveness-report-guide-2.0.pdf");
  }

  print(): void {
    // Render every tab (and expand collapsibles) before the browser snapshots the page.
    this.printing = true;
    this.$nextTick(() => {
      setTimeout(() => {
        window.print();
      }, 400);
    });
  }

  private onBeforePrint = () => {
    this.printing = true;
  };

  private onAfterPrint = () => {
    this.printing = false;
  };

  created() {
    this.reportData = this.report.data as TeamEffectivenessV2ReportData;
    this.resetFilters();
  }

  mounted() {
    window.addEventListener("beforeprint", this.onBeforePrint);
    window.addEventListener("afterprint", this.onAfterPrint);
  }

  beforeDestroy() {
    window.removeEventListener("beforeprint", this.onBeforePrint);
    window.removeEventListener("afterprint", this.onAfterPrint);
  }
}
